import React from 'react';
import useMedicalProfiles from './api/useMedicalProfiles';
import { getIntl } from '../utils/intl';
import { usePromptButton } from '../components/shared/PromptButton';
import { useProfilesTabNavigation } from '../utils/appNavigationNames';
import { ContentStack } from '../components/v2BaseComponents/Stack';
import { PromptActions } from '../components/v2BaseComponents/Prompt';
import InfoIcon from '../components/v2BaseComponents/InfoIcon';

const intl = getIntl('deleteProfileButton', {
    deletePrompt: 'Delete Profile?',
    deleteNotificationHeader: 'Are you sure you want to delete this profile?',
    transferSubtext:
        'If you do not want to lose this information, you may transfer the administration of this Medical Profile to another adult user.',
    transferButton: 'Transfer Administrator',
    deleteSubtext:
        'You have the right to delete this profile and the health information it contains. All data will be permanently deleted and you will not have the option to recover it.',
    deleteButton: 'Yes, Delete This Profile',
    cancelButton: 'No, Cancel',
});

export type UseDeleteProfileButtonArgs = {
    profileId: string;
    onDeleteSuccess: () => void;
};

export type UseDeleteProfileButtonReturnValue = {
    onPress: () => void;
    modal: React.ReactNode;
    isDeletingProfile: boolean;
};

export const useDeleteProfileButton = ({
    profileId,
    onDeleteSuccess,
}: UseDeleteProfileButtonArgs): UseDeleteProfileButtonReturnValue => {
    const { deleteMedicalProfile, isDeletingProfile } = useMedicalProfiles({
        onDeleteMedicalProfileSuccess: onDeleteSuccess,
    });
    const { navigate } = useProfilesTabNavigation();
    const deleteButtonModal = usePromptButton({
        renderPromptContents: (onClose) => (
            <ContentStack>
                <InfoIcon
                    alertIconProps={{
                        name: 'AlertTriangle',
                        variant: 'error',
                    }}
                    title={intl('deleteNotificationHeader')}
                    subText={intl('transferSubtext')}
                />
                <PromptActions
                    actions={[
                        {
                            title: intl('transferButton'),
                            onPress: () => {
                                navigate('add-transfer-request', { profileId });
                                onClose();
                            },
                            icon: 'UserPlus',
                        },
                        {
                            title: intl('deleteButton'),
                            onPress: () => {
                                deleteMedicalProfile(profileId);
                                onClose();
                            },
                            variant: 'alert',
                            icon: 'Trash2',
                            testID: 'delete-profile-prompt-button-confirm',
                        },
                        {
                            title: intl('cancelButton'),
                            onPress: onClose,
                            variant: 'secondary',
                            icon: 'X',
                            testID: 'delete-profile-prompt-button-cancel',
                        },
                    ]}
                />
            </ContentStack>
        ),
        promptTitle: intl('deletePrompt'),
    });

    return {
        onPress: deleteButtonModal.onPress,
        modal: deleteButtonModal.Prompt,
        isDeletingProfile,
    };
};
